// R77 — Sikkens Center bestellijst Excel-export.
// Filter-veld: suppliertitle === "Sikkens Center en Simonis" (robuust;
// dekt automatisch nieuwe merken via dezelfde leverancier). brand_title
// (Trimetal/SPS) blijft als secundaire info beschikbaar in de uitvoer.
import * as XLSX from "xlsx";
import { supabase } from "@/integrations/supabase/client";

export const SIKKENS_CENTER_SUPPLIER = "Sikkens Center en Simonis";

interface RawLine {
  ordernumber: string | null;
  orderid: number | null;
  sku: string | null;
  producttitle: string | null;
  varianttitle: string | null;
  quantityordered: number | null;
  custom_fields: unknown;
  createdat: string | null;
  brand_title: string | null;
  suppliertitle: string | null;
}

interface ExcelRow {
  Ordernummer: string;
  Orderdatum: string;
  SKU: string;
  Product: string;
  Aantal: number;
  Kleurnummer: string;
}

// R77 — datum-format consistent met UI ("30 april 2026").
const ORDER_DATE_FMT = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric", month: "long", year: "numeric",
});

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "" : ORDER_DATE_FMT.format(new Date(t));
}

/** Normaliseer producttitle: vervang " | " (Lightspeed sub-titel separator) door spatie. */
function cleanTitle(t: string): string {
  return t.replace(/\s*\|\s*/g, " ").replace(/\s+/g, " ").trim();
}

/** Parse Kleurnummer uit custom_fields (zelfde logica als pakbon-render: split " |", neem head). */
export function parseKleurnummer(custom_fields: unknown): string {
  if (!Array.isArray(custom_fields)) return "";
  for (const f of custom_fields as any[]) {
    const title = String(f?.title ?? "").trim().toLowerCase();
    if (title !== "kleurnummer") continue;
    const values = Array.isArray(f?.values) ? f.values : [];
    for (const v of values) {
      const raw = v?.value;
      if (raw === false || raw === null || raw === undefined) continue;
      const full = String(raw).trim();
      if (!full) continue;
      return full.split(" |")[0].trim();
    }
  }
  return "";
}

export async function generateSikkensCenterExcel(
  selectedOrdernumbers: string[],
): Promise<{ rowCount: number; orderCount: number; filename: string; loggedOrderids: number[] }> {
  if (selectedOrdernumbers.length === 0) {
    throw new Error("Geen orders geselecteerd");
  }

  const { data, error } = await supabase
    .from("orders_live_ls")
    .select(
      "ordernumber, orderid, sku, producttitle, varianttitle, quantityordered, custom_fields, createdat, brand_title, suppliertitle",
    )
    .in("ordernumber", selectedOrdernumbers)
    .eq("suppliertitle", SIKKENS_CENTER_SUPPLIER)
    .order("ordernumber", { ascending: true })
    .order("lineitemindex", { ascending: true });

  if (error) throw error;

  const lines = (data ?? []) as RawLine[];
  if (lines.length === 0) {
    throw new Error("Geen Sikkens Center / Simonis-regels in geselecteerde orders");
  }

  // R77 — Dedup op (ordernumber, sku, brand_title): sync-artefacten leveren
  // soms 2 rows met licht andere producttitle ("Fix Primer" vs "Fix Primer | Voorstrijkmiddel").
  // Strategie: kies de LANGSTE producttitle (meest complete), som NIET de quantity
  // (zelfde line is dubbel gesynced — qty is identiek per row, niet additief).
  const dedupMap = new Map<string, RawLine>();
  for (const l of lines) {
    const key = `${l.ordernumber ?? ""}::${(l.sku ?? "").trim()}::${(l.brand_title ?? "").trim()}`;
    const existing = dedupMap.get(key);
    if (!existing) {
      dedupMap.set(key, l);
      continue;
    }
    // Behoud de versie met de langste (meest complete) producttitle.
    const existingLen = (existing.producttitle ?? "").length;
    const candidateLen = (l.producttitle ?? "").length;
    if (candidateLen > existingLen) dedupMap.set(key, l);
  }

  const rows: ExcelRow[] = Array.from(dedupMap.values()).map((l) => {
    const brand = (l.brand_title ?? "").trim();
    const title = cleanTitle(l.producttitle ?? "");
    const variant = (l.varianttitle ?? "").trim();
    const productNaam = [brand, title].filter(Boolean).join(" ");
    return {
      Ordernummer: l.ordernumber ?? "",
      Orderdatum: formatDate(l.createdat),
      SKU: (l.sku ?? "").trim(),
      Product: variant ? `${productNaam} — ${variant}` : productNaam,
      Aantal: Number(l.quantityordered ?? 0) || 0,
      Kleurnummer: parseKleurnummer(l.custom_fields),
    };
  });

  const ws = XLSX.utils.json_to_sheet(rows, {
    header: ["Ordernummer", "Orderdatum", "SKU", "Product", "Aantal", "Kleurnummer"],
  });
  ws["!cols"] = [
    { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 80 }, { wch: 8 }, { wch: 32 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sikkens Center");

  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const filename =
    `sikkens-center-bestellijst-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;

  XLSX.writeFile(wb, filename);

  const orderCount = new Set(rows.map((r) => r.Ordernummer)).size;

  // R77: log export per orderid (alleen orders die daadwerkelijk Sikkens-regels bevatten).
  const orderidByNumber = new Map<string, number>();
  for (const l of lines) {
    if (l.ordernumber && l.orderid != null && !orderidByNumber.has(l.ordernumber)) {
      orderidByNumber.set(l.ordernumber, l.orderid);
    }
  }
  const loggedOrderids = Array.from(orderidByNumber.values());
  if (loggedOrderids.length > 0) {
    const logRows = Array.from(orderidByNumber.entries()).map(([ordernumber, orderid]) => ({
      orderid, ordernumber,
    }));
    const { error: logError } = await supabase
      .from("decoprof_sikkens_export_log")
      .insert(logRows);
    if (logError) {
      console.warn("[R77 sikkens-export-log] insert faalde:", logError.message);
    }
  }

  return { rowCount: rows.length, orderCount, filename, loggedOrderids };
}
